import { useEffect, useMemo, useState } from 'react';

import { useCodeTornei } from '@/contexts/CodeTorneiContext';
import { useLimuloPolling, useLimuloPollingGuida } from '@/hooks/useLimuloPolling';
import {
  CR_PROFILE_TYPE,
  eliminatoDalTorneo,
  gameIdsOfProfileType,
  queueListForGames,
  queueMomentOf,
  queueMomentWithTournament,
  ticketOfGames,
} from '@/lib/limulo/code';
import { LIMULO_POLL_LISTA_MS, limuloTournamentPollBase } from '@/lib/limulo/polling';
import {
  useGetLimuloGamesQuery,
  useGetLimuloMatchesQuery,
  useGetLimuloProfilesQuery,
  useGetLimuloQueuesQuery,
  useGetLimuloTournamentQuery,
} from '@/services/limuloApi';

/** Il ritmo degli incontri di «partita trovata»: quello del torneo, piu' lento da eliminati, mai fermo a torneo vivo. */
export function ritmoIncontri(baseTorneo: number, eliminato: boolean): number {
  if (baseTorneo <= 0) return 0;
  return eliminato ? Math.max(baseTorneo, LIMULO_POLL_LISTA_MS) : baseTorneo;
}

/**
 * I DATI di un pannello delle code della Hub CR — la «Ranked» (`CodeTorneiPanel`) e il «Sit&Go»
 * (`SitngoCodePanel`): la vetrina di Clash Royale col suo ritmo, i profili, il biglietto dell'app
 * adottato dalla lista, il momento (col torneo di partita trovata) e il «visto accadere».
 *
 * 🔴 UNO per due pannelli, ed e' la regola del biglietto unico: il biglietto e' uno per gioco
 * (`biglietto.model.ts` di qTournaments), quindi «in coda», «abbinato» e «partita trovata» sono gli
 * STESSI nei due pannelli. Cosa MOSTRARE (le 1v1 o i sit&go) lo decide ciascun pannello con
 * `queuesOfKind`; la lista qui resta intera, perche' i nomi servono a tutti e due.
 * ⚠️ Montato in un pannello alla volta (la Hub rende solo la scheda corrente, `HubSections.tsx`):
 * «visto accadere» vale per il pannello in cui si aspettava.
 *
 * 🔴 PER GIOCO (`profileType`, default Clash Royale): i biglietti sono uno per gioco, e questo pannello racconta
 * solo quello del SUO. Il `code` che rende e' il contesto con `ticket` ristretto al gioco, `leave` che esce da
 * quel gioco e `refusal`/`failure` solo se l'ultima scrittura era su di lui: un'attesa in un altro gioco non e'
 * «Sei in coda» qui. La lista (`lq`) resta intera, e `crIds` si chiama ancora cosi' per chi lo legge.
 */
export function useCodeTorneiPannello(profileType: string = CR_PROFILE_TYPE) {
  const codeApp = useCodeTornei();
  const polling = useLimuloPolling();
  const gq = useGetLimuloGamesQuery(undefined);
  const lq = useGetLimuloQueuesQuery(undefined, polling.ritmo);
  useLimuloPollingGuida(polling, lq, LIMULO_POLL_LISTA_MS);
  const profiliQ = useGetLimuloProfilesQuery(undefined);

  const crIds = useMemo(() => gameIdsOfProfileType(gq.data, profileType), [gq.data, profileType]);
  const lista = useMemo(
    () => (lq.data && crIds ? queueListForGames(lq.data, crIds) : null),
    [lq.data, crIds],
  );

  // Il biglietto di QUESTO gioco, e il contesto visto da questo gioco.
  const bigliettoDelGioco = useMemo(() => ticketOfGames(codeApp.tickets, crIds), [codeApp.tickets, crIds]);
  const { leave, refusal, failure, scrittoSu } = codeApp;
  // `null` = non si sa su che gioco ha scritto (nessuna scrittura ancora): come prima, il rifiuto si mostra.
  const suoi = scrittoSu === null || (crIds !== null && crIds.has(scrittoSu));
  const code = useMemo(
    () => ({
      ...codeApp,
      ticket: bigliettoDelGioco,
      // Senza un biglietto di questo gioco non c'e' niente da lasciare: mai l'ultimo di un altro gioco.
      leave: (queueId?: string) =>
        bigliettoDelGioco === null ? Promise.resolve() : leave(queueId, bigliettoDelGioco.gameId),
      refusal: suoi ? refusal : null,
      failure: suoi ? failure : null,
    }),
    [codeApp, bigliettoDelGioco, leave, suoi, refusal, failure],
  );

  // Riprendere un'attesa (app ricaricata, pannello riaperto): il biglietto che il server ha.
  const { adopt } = codeApp;
  useEffect(() => {
    if (lista !== null && lista.tickets.length > 0) adopt(lista.tickets[0]);
  }, [lista, adopt]);

  const momentoBiglietto = queueMomentOf(code.ticket);
  // Il torneo di `lastMatch`, letto e RILETTO solo a partita trovata (stessa forma di
  // `LimuloTournamentDetail.tsx`, ritmo dedicato: la lista sopra ha il suo). Non arrivata o
  // fallita ⇒ `undefined`: fail-closed. `currentData`, non `data`: la seconda partita sullo stesso
  // montaggio cambia `torneoTrovatoId`, e `data` terrebbe il risultato del torneo PRECEDENTE finche'
  // il nuovo non arriva — un `complete` vecchio che manderebbe all'Elenco un giocatore gia' abbinato
  // di nuovo (misurato in revisione, punto 5).
  const torneoTrovatoId =
    momentoBiglietto.kind === 'partita-trovata' ? momentoBiglietto.tournamentId : null;
  const torneoPolling = useLimuloPolling();
  const torneoTrovatoQ = useGetLimuloTournamentQuery(torneoTrovatoId ?? '', {
    skip: torneoTrovatoId === null,
    refetchOnMountOrArgChange: true,
    ...torneoPolling.ritmo,
  });
  // ⛔ Zero fuori da «partita-trovata» o a torneo terminale: senza ritmo un pannello lasciato
  // aperto su «Partita trovata» non avrebbe mai saputo che il torneo e' finito (misurato in
  // revisione, punto 1 — il reperto di Luca resta rotto senza questo).
  useLimuloPollingGuida(
    torneoPolling,
    torneoTrovatoQ,
    torneoTrovatoId === null ? 0 : limuloTournamentPollBase(torneoTrovatoQ.currentData),
  );
  // L-2436: a torneo vivo si esce da «partita trovata» anche da ELIMINATI (il motore, in
  // eliminazione diretta, lascia rientrare chi ha perso). Il formato e' quello della coda del
  // biglietto. Non letti ⇒ `false` (fail-closed).
  // 🌐 Gli incontri hanno un ritmo A PARTE, con la SUA guida (revisione di #496, 🟡 5): con la guida
  // del torneo un `/matches` in errore non rallentava mai. La base e' quella del torneo (terminale ⇒
  // zero, e smontato non si chiede niente); da ELIMINATI si rallenta a `LIMULO_POLL_LISTA_MS` ma NON
  // ci si ferma (2° giro, 🟡 1, scenario K): se il motore mi da' un turno nuovo — un gruppo svizzero
  // sotto una coda ora a eliminazione diretta — l'incontro nuovo deve rimettere la carta, o e' un forfait.
  const formatoTrovato =
    momentoBiglietto.kind === 'partita-trovata'
      ? ((lista?.queues ?? []).find((q) => q.queueId === momentoBiglietto.queueId)?.format ?? null)
      : null;
  const incontriPolling = useLimuloPolling();
  const incontriTrovatoQ = useGetLimuloMatchesQuery(
    { id: torneoTrovatoId ?? '' },
    { skip: torneoTrovatoId === null, refetchOnMountOrArgChange: true, ...incontriPolling.ritmo },
  );
  const eliminato = eliminatoDalTorneo(formatoTrovato, incontriTrovatoQ.currentData);
  useLimuloPollingGuida(
    incontriPolling,
    incontriTrovatoQ,
    torneoTrovatoId === null ? 0 : ritmoIncontri(limuloTournamentPollBase(torneoTrovatoQ.currentData), eliminato),
  );
  const momento = queueMomentWithTournament(momentoBiglietto, torneoTrovatoQ.currentData, eliminato);
  // ⭐ «Visto accadere»: il giocatore aspettava QUI quando e' stato abbinato. Solo allora, a partita
  // trovata, lo si porta all'incontro da solo (v. `PartitaTrovata`). Un pannello riaperto dopo non
  // l'ha visto, e mostra il bottone.
  const [inDiretta, setInDiretta] = useState(false);
  useEffect(() => {
    if (momento.kind === 'in-attesa' || momento.kind === 'abbinato') setInDiretta(true);
  }, [momento.kind]);
  const nomi = useMemo(() => new Map((lista?.queues ?? []).map((q) => [q.queueId, q])), [lista]);

  return { code, gq, lq, profiliQ, crIds, lista, momento, inDiretta, nomi };
}
